<template>
  <!-- tw-h-8 gives the pill the 32px height of SfButtonDs. The toolbar row has
       no height of its own, so without it the SfButtonInverted inside
       PreparationStatus and ReviewStatus holds the row at 36px with its
       padding. tw-self-center keeps the pill centred beside a taller
       neighbour. -->
  <div
    ref="rootEl"
    class="
      tw-inline-flex
      tw-relative
      tw-h-8
      tw-self-center
      tw-rounded-lg
      tw-border-solid
      tw-border-sf-snow-darker
      tw-border
      tw-overflow-hidden
      tw-shadow-workflow-item-status
    "
  >
    <PreparationStatus
      :preparation="preparation"
      :preparation-loading="preparationLoading"
      :has-any-up-to-date-review="hasAnyUpToDateReview"
      :compact="compact"
      :tracking-prefix="trackingPrefix"
      @toggle-prepared="togglePrepared"
    />

    <ReviewStatus
      :has-any-up-to-date-review="hasAnyUpToDateReview"
      :has-up-to-date-review-by-user="hasUpToDateReviewByUser"
      :review-loading="reviewLoading"
      :reviewed="reviewed"
      :reviews-to-show="reviewsToShow"
      :compact="compact"
      :tracking-prefix="trackingPrefix"
      @toggle-reviewed="toggleReviewed"
    />
  </div>
</template>
<script lang="ts" setup>
import type {
  TemplatePreparation,
  TemplateReview,
} from '@app/application/workflows/types'
import PreparationStatus from './preparation-status.vue'
import ReviewStatus from './review-status.vue'
import useAutoCompact from '@app/shared/vue/composables/use-auto-compact'
import { useDrawer } from '@app/shared/pinia/drawer'
import { RightDrawerContent } from '@app/shared/types/drawer'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

interface Props {
  hasAnyUpToDateReview: boolean
  hasUpToDateReviewByUser: boolean
  reviewed: boolean
  reviewsToShow: Array<TemplateReview>
  preparation: TemplatePreparation | null
  preparationLoading: boolean
  reviewLoading: boolean
  trackingPrefix: string
}

withDefaults(defineProps<Props>(), {
  reviewsToShow: function () {
    return []
  },
  preparation: null,
  preparationLoading: false,
  reviewLoading: false,
})

const emit =
  defineEmits<{
    'toggle-prepared': [boolean]
    'toggle-reviewed': [boolean]
  }>()

const drawerStore = useDrawer()
const { rightDrawerContent } = storeToRefs(drawerStore)

// The documents panel is significantly wider than other right drawers
// (file attachments and file preview), so we collapse the prepare/review
// controls to icon-only when either is active to avoid overflow. Other
// right drawers (audit, communication, ...) keep the expanded view.
const isDocumentsPanelOpen = computed(
  () =>
    rightDrawerContent.value === RightDrawerContent.FileAttachments ||
    rightDrawerContent.value === RightDrawerContent.FilePreview
)

const rootEl = ref<HTMLElement | null>(null)
const compact = useAutoCompact(rootEl, isDocumentsPanelOpen)

function togglePrepared(prepared: boolean) {
  emit('toggle-prepared', prepared)
}

function toggleReviewed(reviewed: boolean) {
  emit('toggle-reviewed', reviewed)
}
</script>
